import axios from 'axios';
import TokenService from './tokenService';
import { ENDPOINTS } from './endpoints';
import { redirectToLogin, redirectToLoginIfUnauthorized } from './authRedirect';
import {
  USERS_API,
  PAYMENTS_API,
  USER_BALANCE_API,
  ADD_BALANCE_API,
  PRICING_API,
  PRICING_CREATE_API,
  PRICING_UPDATE_API,
  EMBEDDED_USERS_API,
  INSERT_USER_API,
  MESSAGE_STATS_ALL_API,
  WHATSAPP_CATALOGS_API,
  WHATSAPP_CATALOG_PRODUCTS_API,
  WHATSAPP_CATALOG_PRODUCT_CREATE_API,
  WHATSAPP_CATALOG_PRODUCT_UPDATE_API,
  WHATSAPP_CATALOG_PRODUCT_UPDATE_INVENTORY_API,
  WHATSAPP_CATALOG_PRODUCT_DELETE_API,
  WHATSAPP_CATALOG_PRODUCT_UPLOAD_IMAGE_API,
} from './adminApiEndpoints';

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

const attachAuthErrorInterceptor = (client) => {
  client.interceptors.response.use(
    (res) => res,
    (error) => {
      redirectToLoginIfUnauthorized(error);
      return Promise.reject(error);
    }
  );
};

attachAuthErrorInterceptor(axios);
attachAuthErrorInterceptor(api);

export const loginUser = (payload) => {
  return axios
    .post(ENDPOINTS.LOGIN, payload, { credentials: 'include' })
    .then((response) => {
      const token = response.data?.token;
      if (!token) throw new Error('No token received from the server.');
      TokenService.setToken(token);
      return response.data;
    })
    .catch((error) => {
      if (error.response) {
        throw new Error(error.response.data?.message || 'Login failed');
      } else if (error.request) {
        throw new Error('No response from the server');
      }
      throw new Error(error.message);
    });
};

export const logoutUser = async () => {
  const token = TokenService.getToken();
  if (!token) return;
  try {
    await axios.post(
      ENDPOINTS.LOGOUT,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      }
    );
  } catch {
    // Still clear local session if the server call fails
  }
};

export const getAuthLogs = async ({
  page = 1,
  limit = 20,
  from_date,
  to_date,
  action = 'all',
  email,
} = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const body = { page, limit, action };
  if (from_date) body.from_date = from_date;
  if (to_date) body.to_date = to_date;
  if (email) body.email = email;
  const res = await api.post(ENDPOINTS.AUTH_LOGS, body, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const forgotPassword = async (email) => {
  try {
    const res = await axios.post(ENDPOINTS.FORGOT_PASSWORD, { email });
    return res.data;
  } catch (error) {
    if (error.response) {
      const data = error.response.data || {};
      throw new Error(data.message || data.error || 'Failed to send reset email');
    }
    throw new Error(error.message || 'Failed to send reset email');
  }
};

export const resetPassword = async ({ token, newPassword }) => {
  try {
    const res = await axios.post(ENDPOINTS.RESET_PASSWORD, {
      token,
      new_password: newPassword,
    });
    return res.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to reset password');
    }
    throw new Error(error.message || 'Failed to reset password');
  }
};

export const getToken = () => {
  const token = TokenService.getToken();
  if (!token) {
    redirectToLogin();
    return null;
  }
  return token;
};

export const getUserBalance = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(
    USER_BALANCE_API,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data; // expect { balance: number }
};

export const getMyInfo = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.get(ENDPOINTS.MY_INFO, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data; // expect { userInfo: { name: string, ... } }
};

export const changePassword = async ({ oldPassword, newPassword }) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  try {
    const res = await api.post(
      ENDPOINTS.CHANGE_PASSWORD,
      {
        old_password: oldPassword,
        new_password: newPassword,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return res.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to change password');
    }
    throw new Error(error.message || 'Failed to change password');
  }
};

export const getAutoReply = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  try {
    const res = await api.post(
      ENDPOINTS.AUTO_REPLY,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to load auto reply settings');
    }
    throw new Error(error.message || 'Failed to load auto reply settings');
  }
};

export const saveAutoReply = async ({ isEnabled, message }) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  try {
    const res = await api.post(
      ENDPOINTS.AUTO_REPLY_SAVE,
      {
        is_enabled: !!isEnabled,
        message,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return res.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || 'Failed to save auto reply settings');
    }
    throw new Error(error.message || 'Failed to save auto reply settings');
  }
};

// Campaign Templates (Summary list) — POST /templates/get-templates
export const fetchCampaignTemplates = async ({
  page = 1,
  limit = 10,
  from_date,
  to_date,
} = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const body = { page, limit };
  if (from_date) body.from_date = from_date;
  if (to_date) body.to_date = to_date;
  const res = await axios.post(ENDPOINTS.GET_CAMP_TEMPLATES(), body, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  // Expecting { templates: [...] }
  return res.data?.templates || [];
};

export const fetchTemplateCounts = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.get(ENDPOINTS.GET_WA_TEMPLATE_COUNTS, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return Array.isArray(res.data?.templateCounts) ? res.data.templateCounts : [];
};

export const createTemplate = async (templateName, templateDescription) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(
    ENDPOINTS.CREATE_TEMPLATE,
    {
      name: templateName,
      description: templateDescription,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  return res.data?.template || res.data;
};

export const fetchWaTemplates = async ({ page = 1, limit = 10 } = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.get(ENDPOINTS.FETCH_TEMPLATES(page, limit), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data || { templates: [] };
};

export const createWaTemplate = async (payload) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(ENDPOINTS.CREATE_WA_TEMPLATE, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  return res.data;
};

export const uploadTemplateMedia = async (file) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const formData = new FormData();
  formData.append('mediaFile', file);

  const res = await api.post(ENDPOINTS.UPLOAD_TEMPLATE_MEDIA, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
};

export const fetchWaApTemplates = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.get(ENDPOINTS.GET_WA_AP_TEMPLATES, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const mapTemplateToCampaign = async ({ templateId, waTemplateId, userId }) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(
    ENDPOINTS.TEMPLATE_MAPPING,
    {
      template_id: templateId,
      wa_template_id: waTemplateId,
      userid: userId,
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const uploadCampaignMedia = async (file) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const formData = new FormData();
  formData.append('mediaFile', file);

  const res = await api.post(ENDPOINTS.UPLOAD_CAMPAIGN_MEDIA, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
};

export const sendCampaignMessage = async (payload = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(ENDPOINTS.SEND_CAMPAIGN_MESSAGE, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const sendCustomizedCampaignMessage = async (formData) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(ENDPOINTS.SEND_CUSTOMISED_CAMPAIGN_MESSAGE, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
};

export const getCampaignReport = async (templateId) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(
    ENDPOINTS.FETCH_REPORT(templateId),
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const getStatusData = async (templateId, status, page = 1, limit = 10) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await axios.post(
    ENDPOINTS.FETCH_STATUS(templateId, status, page, limit),
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Detailed message report (paginated)
export const getCampaignMessageReport = async (body = {}, page = 1, limit = 10) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ENDPOINTS.GET_CAMPAIGN_REPORT,
    { ...body, page, limit },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data || { data: [], pagination: { total_pages: 1, current_page: 1 } };
};

// Full detailed message report for CSV download
export const getCampaignMessageFullReport = async (body = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ENDPOINTS.GET_CAMPAIGN_REPORT,
    { ...body, page: 1, limit: 50000 },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data?.data || [];
};

// Number message report
export const getNumberMessageReport = async (body = {}, page = 1, limit = 10) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ENDPOINTS.NUMBER_MESSAGE_REPORT,
    { ...body, page, limit },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data || { data: [], pagination: { total_pages: 1, current_page: 1 } };
};

// User payments (logged-in user from token)
export const getUserPayments = async ({
  page = 1,
  limit = 10,
  transactionType,
  fromDate,
  toDate,
} = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const body = { page, limit };
  if (transactionType) body.transaction_type = transactionType;
  if (fromDate) body.from_date = fromDate;
  if (toDate) body.to_date = toDate;
  const res = await api.post(PAYMENTS_API, body, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

/** Payments for a specific user (admin) — PAYMENTS_API + `user_id` in body (legacy admin). */
export const getUserPaymentsForUser = async (userId, page = 1, limit = 100) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    PAYMENTS_API,
    { user_id: Number(userId), page, limit },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Admin APIs
export const getUsers = async (page = 1, limit = 10) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    USERS_API,
    { page, limit },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

/** Current user message stats for a period (`POST /user/message-stats`). */
export const getMessageStats = async ({ period, fromDate, toDate }) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const body = { period };
  if (period === 'custom') {
    body.from_date = fromDate;
    body.to_date = toDate;
  }
  const res = await api.post(ENDPOINTS.MESSAGE_STATS, body, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  return res.data;
};

/** Aggregated dashboard analytics (`POST /user/dashboard-analytics`). */
export const getDashboardAnalytics = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ENDPOINTS.DASHBOARD_ANALYTICS,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  return res.data;
};

/** Page size for admin message-stats table (`POST /user/message-stats-all`). */
export const MESSAGE_STATS_PAGE_SIZE = 10;

/** Admin: all users’ message stats for a period (`POST /user/message-stats-all`). */
export const getMessageStatsAll = async ({
  period,
  fromDate,
  toDate,
  page = 1,
  limit = MESSAGE_STATS_PAGE_SIZE,
}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const body = {
    period,
    page: Number(page) || 1,
    limit: Number(limit) || MESSAGE_STATS_PAGE_SIZE,
  };
  if (period === 'custom') {
    body.from_date = fromDate;
    body.to_date = toDate;
  }
  const res = await api.post(
    MESSAGE_STATS_ALL_API,
    body,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  return res.data;
};

export const updateUserStatus = async (userId, status) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.put(
    `${USERS_API}/${userId}`,
    { status },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const getPricingOptions = async (opts = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    PRICING_API,
    { page: opts.page || 1, limit: opts.limit || 100 },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const createPricingTier = async (payload) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(PRICING_CREATE_API, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const updatePricingTier = async (id, payload) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.put(PRICING_UPDATE_API(id), payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const getEmbeddedUsers = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    EMBEDDED_USERS_API,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const createManagedUser = async (payload) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    INSERT_USER_API,
    payload,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const getWhatsAppCatalogs = async (catalogId, options = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const body = { link_if_missing: true };
  if (catalogId) body.catalog_id = catalogId;
  if (options?.target_user_id) body.target_user_id = options.target_user_id;
  const res = await api.post(WHATSAPP_CATALOGS_API, body, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const getWhatsAppCatalogProducts = async (catalogId, after, options = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const body = { catalog_id: catalogId };
  if (after) body.after = after;
  if (options?.target_user_id) body.target_user_id = options.target_user_id;
  const res = await api.post(WHATSAPP_CATALOG_PRODUCTS_API, body, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const createWhatsAppCatalogProduct = async (payload) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const { phone_number_id, ...rest } = payload || {};
  const res = await api.post(WHATSAPP_CATALOG_PRODUCT_CREATE_API, rest, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const updateWhatsAppCatalogProduct = async (payload) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const { phone_number_id, ...rest } = payload || {};
  const res = await api.post(WHATSAPP_CATALOG_PRODUCT_UPDATE_API, rest, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const updateWhatsAppCatalogProductInventory = async (payload) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const { phone_number_id, ...rest } = payload || {};
  const res = await api.post(WHATSAPP_CATALOG_PRODUCT_UPDATE_INVENTORY_API, rest, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const deleteWhatsAppCatalogProduct = async (productId, options = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const body = { product_id: productId };
  if (options?.target_user_id) body.target_user_id = options.target_user_id;
  const res = await api.post(WHATSAPP_CATALOG_PRODUCT_DELETE_API, body, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const uploadWhatsAppCatalogImage = async (file) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const formData = new FormData();
  formData.append('image', file);
  const res = await api.post(WHATSAPP_CATALOG_PRODUCT_UPLOAD_IMAGE_API, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
};

export const getUserBalanceById = async (userId) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    USER_BALANCE_API,
    { user_id: Number(userId) },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const addUserBalance = async (userId, amount) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ADD_BALANCE_API,
    { user_id: Number(userId), amount: Number(amount) },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Chat APIs
export const getChatHistory = async (waId, page = 1, limit = 20) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ENDPOINTS.GET_CHAT_HISTORY,
    { wa_id: waId, page, limit },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

export const fetchMediaBlob = async (mediaId) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.get(ENDPOINTS.DOWNLOAD_MEDIA(mediaId), {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob',
  });
  return res.data;
};

export const sendMessageAPI = async (wa_id, message_body) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ENDPOINTS.SEND_WHATSAPP,
    { wa_id, message_body },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data;
};

// Bot flow responses
export const getUserFlows = async (page = 1, limit = 10) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.get(ENDPOINTS.GET_USER_FLOWS, {
    params: { page, limit },
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const getFlowResponsesByType = async (flowType, page = 1, limit = 10) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.get(ENDPOINTS.GET_FLOW_RESPONSES, {
    params: { flowType, page, limit },
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// Incoming messages list
export const getIncomingMessages = async (page = 1, limit = 10, fromDate, toDate) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(
    ENDPOINTS.GET_INCOMING_MESSAGES(page, limit, fromDate, toDate),
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return res.data; // expect { messages: [...], pagination? }
};

// Dynamic bot builder
export const listBotDefinitions = async () => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.get(ENDPOINTS.BOT_DEFINITIONS, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const getBotDefinition = async (id) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.get(ENDPOINTS.BOT_DEFINITION(id), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const createBotDefinition = async (payload = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(ENDPOINTS.BOT_DEFINITIONS, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const updateBotDefinition = async (id, payload) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.put(ENDPOINTS.BOT_DEFINITION(id), payload, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const publishBotDefinition = async (id) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(ENDPOINTS.BOT_PUBLISH(id), {}, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const archiveBotDefinition = async (id) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.post(ENDPOINTS.BOT_ARCHIVE(id), {}, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const validateBotDefinition = async (id) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.get(ENDPOINTS.BOT_VALIDATE(id), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const getBotRunLogs = async (id, limit = 50) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.get(ENDPOINTS.BOT_LOGS(id), {
    params: { limit },
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const listMetaFlows = async (publishedOnly = true) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.get(ENDPOINTS.BOT_META_FLOWS, {
    params: { publishedOnly },
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const listMetaFlowScreens = async (flowId) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await api.get(ENDPOINTS.BOT_META_FLOW_SCREENS(flowId), {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};
